import React, { useEffect, useState } from 'react';

import { NamedLink } from '../../components';
import { DEALER } from '../audience';
import { regionVon, NORD, SUED } from '../geo';
import { tagesaussicht, temperaturText, aufkommenHinweis, clubHinweis } from '../weather';
import { spruchFuer } from '../motivation';
import { holeWetter } from './wetterAbfrage';
import WetterIcon from './WetterIcon';

import css from './SectionWelcome.module.css';

/**
 * FairwayBid – der Empfang nach der Anmeldung
 *
 * Vorher stand hier ein Absatz Fließtext: "Willkommen zurück. Ihre
 * laufenden Ausschreibungen finden Sie unter Anfragen …". Er war freundlich
 * und vollkommen inhaltsleer - wer angemeldet ist, weiß, wo seine Anfragen
 * sind. Kacheln statt Absatz, und eine davon sagt etwas, das man wirklich
 * noch nicht weiß.
 *
 * Warum Wetter: Bei Golfanlagen bestimmt es den Tagesablauf. Wer bei Sonne
 * auf dem Platz ist, sitzt bei Regen im Büro - und im Büro werden
 * Ausschreibungen geschrieben. Für die Anlage ist es der eigene Tag, für
 * den Händler die Erwartung, wie viel heute hereinkommt.
 */

/**
 * Datum und Uhrzeit.
 *
 * Erst nach dem Aufbau: Auf dem Server ist es eine andere Sekunde als im
 * Browser, und eine Uhr, die sich beim Hydrieren um eine Minute korrigiert,
 * ist ein Hydration-Fehler mit Ansage.
 */
const useJetzt = () => {
  const [jetzt, setJetzt] = useState(null);

  useEffect(() => {
    setJetzt(new Date());
    // Jede halbe Minute. Sekundengenau waere eine Uhr, hier reicht die Zeit.
    const timer = setInterval(() => setJetzt(new Date()), 30000);
    return () => clearInterval(timer);
  }, []);

  return jetzt;
};

/** Holt die Tagesprognose für einen oder zwei Punkte. */
const useWetter = punkte => {
  const [daten, setDaten] = useState({});
  const schluessel = punkte.map(p => `${p.lat},${p.lon}`).join('|');

  useEffect(() => {
    let abgebrochen = false;
    Promise.all(punkte.map(p => holeWetter(p.lat, p.lon))).then(antworten => {
      if (abgebrochen) return;
      const neu = {};
      punkte.forEach((p, i) => {
        neu[p.key] = tagesaussicht(antworten[i]);
      });
      setDaten(neu);
    });
    return () => {
      abgebrochen = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schluessel]);

  return daten;
};

const Lage = ({ aussicht, ort }) => {
  if (!aussicht) {
    return (
      <div className={css.lage}>
        <span className={css.lageOrt}>{ort}</span>
        <span className={css.lageLaedt}>wird geladen …</span>
      </div>
    );
  }

  const temperatur = temperaturText(aussicht);

  return (
    <div className={css.lage}>
      <WetterIcon symbol={aussicht.symbol} titel={aussicht.label} />
      <div className={css.lageText}>
        <span className={css.lageOrt}>{ort}</span>
        <span className={css.lageWert}>
          {aussicht.label}
          {temperatur ? ` · ${temperatur}` : ''}
        </span>
      </div>
    </div>
  );
};

/**
 * Die Statuszeile unter der Überschrift.
 *
 * Sie zeigt eine echte Zahl. Ein "Systeme bereit" ohne Inhalt wäre Kulisse,
 * und Kulisse fliegt beim zweiten Blick auf.
 */
const statusText = (offeneAnfragen = null) =>
  offeneAnfragen === null
    ? 'Kategorien werden abgefragt …'
    : offeneAnfragen === 0
    ? 'Derzeit keine offene Anfrage in Ihren Kategorien'
    : offeneAnfragen === 1
    ? '1 offene Anfrage in Ihren Kategorien'
    : `${offeneAnfragen} offene Anfragen in Ihren Kategorien`;

const SectionWelcome = ({ audience, currentUser, offeneAnfragen = null }) => {
  const istHaendler = audience === DEALER;
  const jetzt = useJetzt();

  // Die Anlage bekommt ihr eigenes Wetter, der Haendler Nord und Sued.
  const region = istHaendler ? null : regionVon(currentUser);
  const punkte = istHaendler
    ? [{ key: 'nord', ...NORD }, { key: 'sued', ...SUED }]
    : region
    ? [{ key: 'hier', lat: region.lat, lon: region.lon }]
    : [];

  const wetter = useWetter(punkte);
  const hinweis = istHaendler
    ? aufkommenHinweis(wetter.nord, wetter.sued)
    : { ton: 'club', text: clubHinweis(wetter.hier) };

  const name = currentUser?.attributes?.profile?.displayName || null;

  const kacheln = (
    <div className={css.grid}>
      {/* Kachel 1: der Tag. */}
      <article className={css.tile}>
        <span className={css.eyebrow}>Heute</span>
        {/*
          Beide Schreibweisen stehen da, die Breite entscheidet - so muss
          kein Skript die Fenstergroesse messen, und beim Drehen des
          Telefons stimmt es sofort.
        */}
        <p className={css.datum}>
          <span className={css.datumLang}>
            {jetzt
              ? jetzt.toLocaleDateString('de-DE', {
                  weekday: 'long',
                  day: 'numeric',
                  month: 'long',
                })
              : ' '}
          </span>
          <span className={css.datumKurz}>
            {jetzt
              ? jetzt.toLocaleDateString('de-DE', {
                  weekday: 'short',
                  day: 'numeric',
                  month: 'short',
                })
              : ' '}
          </span>
        </p>
        <p className={css.uhrzeit}>
          {jetzt
            ? `${jetzt.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })} Uhr`
            : ' '}
        </p>
        {name ? <p className={css.gruss}>Willkommen zurück, {name}.</p> : null}

        {/*
          Der Satz des Tages - nur fuer Haendler, und nur wenn die Uhr schon
          steht: Er haengt am Datum, und Server und Browser koennten um
          Mitternacht verschiedene Tage haben.
        */}
        {istHaendler && jetzt ? <p className={css.spruch}>{spruchFuer(jetzt)}</p> : null}
      </article>

      {/* Kachel 2: das Wetter - fuer die Anlage ihres, fuer den Haendler das Land. */}
      <article className={css.tile}>
        <span className={css.eyebrow}>
          {istHaendler ? 'Wetter in Deutschland' : 'Wetter bei Ihnen'}
        </span>

        {istHaendler ? (
          <div className={css.lagen}>
            <Lage aussicht={wetter.nord} ort="Norden" />
            <Lage aussicht={wetter.sued} ort="Süden" />
          </div>
        ) : region ? (
          <div className={css.lagen}>
            <Lage aussicht={wetter.hier} ort={region.name} />
          </div>
        ) : (
          <p className={css.fehlt}>
            Für die Wetteraussicht brauchen wir Ihre Postleitzahl.{' '}
            <NamedLink name="ManageAccountPage">Im Konto ergänzen</NamedLink>
          </p>
        )}

        {hinweis?.text ? (
          <p className={istHaendler ? css.prognose : css.clubsatz}>{hinweis.text}</p>
        ) : null}
      </article>
    </div>
  );

  /*
    Eine dritte Kachel gab es hier einmal: "Ihr naechster Schritt" mit dem
    Knopf zu den offenen Anfragen. Sie ist raus, weil sie Wort fuer Wort
    dasselbe sagte wie das Periodenbanner darunter - bei Haendlern sogar
    denselben Satz, bei Anlagen denselben Knopf. Der Aufruf gehoert ins
    Banner: Dort steht der Grund daneben.
  */
  if (!istHaendler) {
    return <section className={css.root}>{kacheln}</section>;
  }

  return (
    <section className={css.root}>
      {/*
        Eine Überschrift, die benennt, wo man ist - mehr braucht es nicht.
        Hier stand einmal ein animierter Schriftzug quer und dann hochkant
        an der Kante; beides zog Aufmerksamkeit auf sich, die den Kacheln
        darunter fehlte.
      */}
      <h2 className={css.portalTitel}>Händlerportal</h2>

      <p className={css.status}>
        <span className={css.statusPunkt} aria-hidden="true" />
        {statusText(offeneAnfragen)}
      </p>

      {kacheln}
    </section>
  );
};

export default SectionWelcome;
